import React, { useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { createUseStyles, Loader, SearchBar, Button, SelectWrapper } from '@Sage/hoi-poi-ui';
import Fuse from 'fuse.js';
import { useWeb5Context } from "../../../../../lib/Web5Context";
import { removeAgent } from "../../../../../lib/services/copilotAgents";
import { errorToast } from "../../../../../utils/toast";
import { cleanString } from "../../../../../utils/string";
import AbsoluteView from "../../../../../components/AbsoluteView";
import CopilotSidebarTabHeader from "../../components/CopilotSidebarTabHeader";
import CopilotSidebarAgent from "./CopilotSidebarAgent";
import EmptyAgentsImage from "./EmptyAgentsImage";
import { CopilotContext } from "../../../Context";
import styles from "./styles";
const useStyles = createUseStyles(styles, 'CopilotSidebarAgentsList');
const FUSE_OPTIONS = {
  threshold: 0.2,
  keys: ['name', 'description'],
  getFn: (obj, path) => {
    const value = Fuse.config.getFn(obj, path);
    return cleanString(value);
  }
};
var _ref3 = /*#__PURE__*/React.createElement(Loader, {
  size: "big"
});
var _ref4 = /*#__PURE__*/React.createElement(EmptyAgentsImage, null);
const CopilotSidebarAgentsList = _ref => {
  let {
    createAgent,
    updateAgent
  } = _ref;
  const {
    t
  } = useWeb5Context();
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const agentsRef = useRef([]);
  const timeoutRef = useRef(null);
  const [searchValue, setSearchValue] = useState('');
  const {
    copilotAgents,
    refreshAgents
  } = useContext(CopilotContext);
  useEffect(() => {
    const agents = copilotAgents.filter(agent => agent.type !== 'system');
    agentsRef.current = agents;
    setAgents(agents);
    setLoading(false);
  }, [copilotAgents, t]);
  const onRemoveAgent = useCallback(agent => {
    removeAgent(agent.id).then(() => {
      refreshAgents();
    }).catch(error => {
      console.error(error);
      errorToast({
        t,
        title: t('cfm_label_error_generic')
      });
    });
  }, [refreshAgents, t]);
  const onSearch = useCallback(value => {
    var _value$target;
    clearTimeout(timeoutRef.current);
    const searchValue = value === null || value === void 0 ? void 0 : (_value$target = value.target) === null || _value$target === void 0 ? void 0 : _value$target.value;
    setSearchValue(searchValue);
    if (!searchValue) {
      setAgents(agentsRef.current);
      return;
    }
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      const fuse = new Fuse(agentsRef.current, FUSE_OPTIONS);
      const matchingOptions = fuse.search(searchValue.toLowerCase()).map(result => result.item);
      setAgents(matchingOptions);
    }, 300);
  }, []);
  const newButtonItems = useMemo(() => {
    return [{
      label: 'Custom agent',
      value: 'custom'
    }, {
      label: 'Remote agent (URL)',
      value: 'a2a'
    }];
  }, []);
  const showLoading = loading && agents.length === 0;
  const isEmpty = !loading && agents.length === 0;
  const getSideComponent = useCallback(buttonType => {
    const selectWrapperProps = {
      children: /*#__PURE__*/React.createElement(Button, {
        type: buttonType
      }, "Create Agent"),
      options: newButtonItems,
      onChange: _ref2 => {
        let {
          value
        } = _ref2;
        return createAgent(value);
      },
      overrides: {
        Popover: {
          root: {
            placement: 'bottomRight'
          }
        }
      }
    };
    return /*#__PURE__*/React.createElement(SelectWrapper, selectWrapperProps);
  }, [createAgent, newButtonItems]);
  const headerCreateAgent = useMemo(() => {
    return getSideComponent('primary');
  }, [getSideComponent]);
  const emptyCreateAgent = useMemo(() => {
    return getSideComponent('secondary');
  }, [getSideComponent]);
  return /*#__PURE__*/React.createElement("div", {
    className: classes.root
  }, /*#__PURE__*/React.createElement(CopilotSidebarTabHeader, {
    title: "Agents",
    sideComponent: headerCreateAgent
  }), showLoading && /*#__PURE__*/React.createElement("div", {
    className: classes.loading
  }, _ref3), !showLoading && /*#__PURE__*/React.createElement("div", {
    className: classes.agentsContainer
  }, /*#__PURE__*/React.createElement(SearchBar, {
    placeholder: "Search agent",
    useAsSimpleSearch: true,
    onChange: onSearch
  }), /*#__PURE__*/React.createElement("div", {
    className: classes.agents
  }, agents === null || agents === void 0 ? void 0 : agents.map(agent => /*#__PURE__*/React.createElement(CopilotSidebarAgent, {
    key: agent.id,
    agent: agent,
    classes: classes,
    onClick: updateAgent,
    onRemove: onRemoveAgent
  })))), isEmpty && /*#__PURE__*/React.createElement(AbsoluteView, {
    className: classes.emptyView,
    emptyImage: _ref4,
    title: !searchValue ? 'Agents not created yet' : 'No search results found',
    subtitle: !searchValue ? "It looks like you haven't created any agents yet. Start from scratch to create one or add a remote agent!" : 'No search results found. Please try a different search term.',
    button: emptyCreateAgent
  }));
};
export default CopilotSidebarAgentsList;